import { isConsumedId } from '../../dux/modules/sharedEndpoints'

/*
 * "Apply my changes to all subfolders" for the folder permissions dialog.
 *
 * What propagates is the DELTA — the roles the admin added or removed for
 * each user/group in this dialog — never a copy of the folder's whole
 * permission set, so every OTHER entry on each subfolder is left as it was.
 *
 * Pure functions: the dialog computes the result and sends it as a single
 * BULK_CONTENTROLE_UPDATE action, however large the tree.
 */

const TRAVERSER = 'traverser'
const SYS_TRAVERSERS = 'SYS.traversers'

const roleSet = (entry) => new Set((entry && entry.roleIds) || [])

/**
 * Per-entity roles added and removed between two contentRoles maps.
 * Entities whose roles did not change are omitted.
 *
 * @returns {Object<string, {entityType, entityId, add: string[], remove: string[]}>}
 */
export const computeRoleDelta = (original = {}, final = {}) => {
  const delta = {}
  const entities = new Set([...Object.keys(original), ...Object.keys(final)])

  entities.forEach((entity) => {
    const before = roleSet(original[entity])
    const after = roleSet(final[entity])
    const add = [...after].filter((r) => !before.has(r))
    const remove = [...before].filter((r) => !after.has(r))
    if (add.length === 0 && remove.length === 0) return

    const source = final[entity] || original[entity] || {}
    delta[entity] = {
      entityType: source.entityType,
      entityId: source.entityId || entity,
      add,
      remove,
    }
  })

  return delta
}

/**
 * Every folder beneath rootId (not rootId itself), breadth-first. Skips
 * deleted folders, dangling ids, and consumed shared-endpoint folders —
 * those are read-only here and inherit their permissions from the host.
 */
export const collectDescendants = (rootId, byId) => {
  const out = []
  const seen = new Set([rootId])
  const queue = [...((byId[rootId] && byId[rootId].childFolders) || [])]

  while (queue.length) {
    const id = queue.shift()
    if (seen.has(id)) continue
    seen.add(id)
    const folder = byId[id]
    if (!folder || folder.deleted || isConsumedId(id)) continue
    out.push(id)
    queue.push(...(folder.childFolders || []))
  }

  return out
}

/**
 * True if some folder strictly beneath folderId gives `entity` a role other
 * than traverser — i.e. the entity still needs traverser on folderId to get
 * there. Null-safe on dangling child ids (unlike requiresTraverserRole).
 */
const subtreeNeedsTraverse = (folderId, entity, rolesFor, byId) => {
  if (entity === SYS_TRAVERSERS) return true
  const stack = [...((byId[folderId] && byId[folderId].childFolders) || [])]
  const seen = new Set()
  while (stack.length) {
    const id = stack.pop()
    if (seen.has(id)) continue
    seen.add(id)
    const folder = byId[id]
    if (!folder || folder.deleted) continue
    const entry = rolesFor(id)[entity]
    if (entry && (entry.roleIds || []).some((r) => r !== TRAVERSER)) return true
    stack.push(...(folder.childFolders || []))
  }
  return false
}

const sameRoles = (a = {}, b = {}) => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const k of keys) {
    const x = roleSet(a[k])
    const y = roleSet(b[k])
    if (x.size !== y.size || [...x].some((r) => !y.has(r))) return false
  }
  return true
}

/**
 * Apply a role delta to every descendant of rootId.
 *
 * @returns {{
 *   updates: Object<string, Object>,   // folderId -> new contentRoles (changed folders only)
 *   considered: number,                // descendants eligible for the cascade
 *   changed: number,
 *   perEntity: Object<string, {added: number, removed: number}>,
 * }}
 */
export const applyDeltaToSubtree = (rootId, byId, delta) => {
  const descendants = collectDescendants(rootId, byId)
  const entities = Object.keys(delta)
  const next = {}
  const perEntity = {}
  entities.forEach((e) => {
    perEntity[e] = { added: 0, removed: 0 }
  })

  // Pass 1: add/remove the edited roles
  descendants.forEach((id) => {
    const roles = { ...(byId[id].contentRoles || {}) }
    entities.forEach((entity) => {
      const { add, remove, entityType, entityId } = delta[entity]
      const set = roleSet(roles[entity])
      let touched = false
      add.forEach((r) => {
        if (!set.has(r)) {
          set.add(r)
          touched = true
        }
      })
      if (touched) perEntity[entity].added += 1
      let removedHere = false
      remove.forEach((r) => {
        if (set.has(r)) {
          set.delete(r)
          removedHere = true
        }
      })
      if (removedHere) perEntity[entity].removed += 1

      if (set.size === 0) delete roles[entity]
      else
        roles[entity] = {
          ...(roles[entity] || {}),
          entityType: (roles[entity] && roles[entity].entityType) || entityType,
          entityId: (roles[entity] && roles[entity].entityId) || entityId,
          roleIds: [...set],
        }
    })
    next[id] = roles
  })

  // Pass 2: an entity stripped of every role on a folder still needs
  // traverser there if something deeper still grants it a real role.
  const rolesFor = (id) => next[id] || (byId[id] && byId[id].contentRoles) || {}
  descendants.forEach((id) => {
    entities.forEach((entity) => {
      if (next[id][entity]) return
      if (subtreeNeedsTraverse(id, entity, rolesFor, byId)) {
        const { entityType, entityId } = delta[entity]
        next[id][entity] = { entityType, entityId, roleIds: [TRAVERSER] }
      }
    })
  })

  const updates = {}
  descendants.forEach((id) => {
    if (!sameRoles(byId[id].contentRoles || {}, next[id]))
      updates[id] = next[id]
  })

  return {
    updates,
    considered: descendants.length,
    changed: Object.keys(updates).length,
    perEntity,
  }
}
